import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboardIcon, CalendarDaysIcon, ActivityIcon, FileTextIcon, UsersIcon, CpuIcon, CalendarIcon, UserCircleIcon, FileCodeIcon, SettingsIcon, XIcon, MenuIcon, ChevronRightIcon, LogOutIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRole, Role } from '../../hooks/useRole';
import { useIsTablet } from '../../hooks/useMediaQuery';
const navItems = [{
  path: '/',
  label: 'Dashboard',
  icon: LayoutDashboardIcon,
  roles: ['superadmin', 'medico']
}, {
  path: '/bookings',
  label: 'Prenotazioni',
  icon: CalendarDaysIcon,
  roles: ['superadmin', 'medico']
}, {
  path: '/exams',
  label: 'Esami e Referti',
  icon: ActivityIcon,
  roles: ['superadmin', 'medico']
}, {
  path: '/doctors',
  label: 'Medici',
  icon: UsersIcon,
  roles: ['superadmin']
}, {
  path: '/machines',
  label: 'Macchinari',
  icon: CpuIcon,
  roles: ['superadmin']
}, {
  path: '/calendar',
  label: 'Calendario',
  icon: CalendarIcon,
  roles: ['superadmin', 'medico']
}, {
  path: '/patients',
  label: 'Pazienti',
  icon: UserCircleIcon,
  roles: ['superadmin', 'medico']
}, {
  path: '/templates',
  label: 'Modelli Referto',
  icon: FileCodeIcon,
  roles: ['superadmin', 'medico']
}, {
  path: '/patient-reports',
  label: 'I miei referti',
  icon: FileTextIcon,
  roles: ['paziente']
}, {
  path: '/patient-exams',
  label: 'I miei esami',
  icon: CalendarIcon,
  roles: ['paziente']
}];
interface SidebarProps {
  isMobileOpen: boolean;
  isTabletOpen: boolean;
  onCloseMobile: () => void;
  onToggleTablet: () => void;
  onCloseTablet: () => void;
}
export function Sidebar({
  isMobileOpen,
  isTabletOpen,
  onCloseMobile,
  onToggleTablet,
  onCloseTablet
}: SidebarProps) {
  const {
    role,
    setRole
  } = useRole();
  const navigate = useNavigate();
  const isTablet = useIsTablet();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const filteredNavItems = navItems.filter(item => item.roles.includes(role));
  const handleNavClick = () => {
    onCloseMobile();
    if (isTablet) {
      onCloseTablet();
    }
  };
  const handleRoleSwitch = (newRole: Role) => {
    setRole(newRole);
    if (newRole === 'paziente') {
      navigate('/patient-reports');
    } else {
      navigate('/');
    }
  };
  // Full sidebar content (expanded state) — used for mobile overlay and tablet overlay
  const sidebarContentFull = <aside className="bg-white shadow-sidebar flex flex-col z-40 h-full w-[260px]" data-id="element-64">
      {/* Logo Area */}
      <div className="h-16 flex items-center border-b border-clinical-gray-100 shrink-0 px-6" data-id="element-65">
        <div className="flex flex-col justify-center w-full" data-id="element-66">
          <div className="flex items-center justify-between" data-id="element-67">
            <div className="flex items-start" data-id="element-68">
              <span className="text-xl font-bold text-text-primary tracking-tight" data-id="element-69">
                HEALTH
              </span>
              <span className="text-sm font-bold text-medical-green leading-none mt-1" data-id="element-70">
                1
              </span>
            </div>
            {/* Close Button — mobile & tablet */}
            <button onClick={isTablet ? onCloseTablet : onCloseMobile} className="p-2 text-text-secondary hover:bg-clinical-gray rounded-full transition-colors" aria-label="Chiudi menu" data-id="element-71">
              <XIcon size={20} data-id="element-72" />
            </button>
          </div>
          <span className="text-xs text-text-secondary font-medium truncate" data-id="element-73">
            Modulo Diagnostico
          </span>
        </div>
      </div>

      {/* Role Switcher — shown on tablet inside sidebar */}
      {isTablet && <div className="px-3 pt-4 pb-2" data-id="element-74">
          <p className="text-[10px] uppercase tracking-wider text-text-tertiary font-medium px-3 mb-2" data-id="element-75">
            Vista
          </p>
          <div className="flex items-center bg-clinical-gray-100 p-1 rounded-lg border border-clinical-gray-200" data-id="element-76">
            <button onClick={() => handleRoleSwitch('superadmin')} className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${role === 'superadmin' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-77">
              Superadmin
            </button>
            <button onClick={() => handleRoleSwitch('medico')} className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${role === 'medico' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-78">
              Medico
            </button>
            <button onClick={() => handleRoleSwitch('paziente')} className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${role === 'paziente' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-79">
              Paziente
            </button>
          </div>
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 no-scrollbar" data-id="element-80">
        {filteredNavItems.map(item => <NavLink key={item.path} to={item.path} onClick={handleNavClick} className={({
        isActive
      }) => `
              flex items-center gap-3 px-3 py-2.5 rounded-md text-body font-medium transition-colors group relative min-h-[44px]
              ${isActive ? 'bg-medical-green-50 text-medical-green' : 'text-text-secondary hover:bg-clinical-gray hover:text-text-primary'}
            `} data-id="element-81">
            {({
          isActive
        }) => <>
                {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-medical-green rounded-r-full" data-id="element-82" />}
                <item.icon size={20} className={`shrink-0 ${isActive ? 'text-medical-green' : 'text-text-tertiary group-hover:text-text-secondary'}`} data-id="element-83" />
                <span className="truncate" data-id="element-84">{item.label}</span>
              </>}
          </NavLink>)}
      </nav>

      {/* User Profile Area — enhanced on tablet with settings access */}
      <div className="border-t border-clinical-gray-100 shrink-0" data-id="element-85">
        {isTablet ? <div className="p-3" data-id="element-86">
            <button onClick={() => setProfileMenuOpen(!profileMenuOpen)} className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-clinical-gray transition-colors cursor-pointer min-h-[44px] w-full text-left" data-id="element-87">
              <div className="w-8 h-8 shrink-0 rounded-full bg-medical-green text-white flex items-center justify-center text-sm font-medium" data-id="element-88">
                {role === 'superadmin' ? 'SA' : role === 'medico' ? 'MD' : 'PZ'}
              </div>
              <div className="flex-1 min-w-0" data-id="element-89">
                <p className="text-sm font-medium text-text-primary truncate capitalize" data-id="element-90">
                  {role}
                </p>
                <p className="text-xs text-text-secondary truncate" data-id="element-91">
                  {role === 'superadmin' ? 'Amministratore di sistema' : role === 'medico' ? 'Medico Refertatore' : 'Area Personale'}
                </p>
              </div>
              <ChevronRightIcon size={16} className={`text-text-tertiary shrink-0 transition-transform duration-200 ${profileMenuOpen ? 'rotate-90' : ''}`} data-id="element-92" />
            </button>

            <AnimatePresence data-id="element-93">
              {profileMenuOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className="overflow-hidden" data-id="element-94">
                  <div className="pt-1 space-y-0.5" data-id="element-95">
                    {role !== 'paziente' && <button onClick={() => {
                navigate('/settings');
                onCloseTablet();
                setProfileMenuOpen(false);
              }} className="w-full text-left px-3 py-2.5 text-sm text-text-primary hover:bg-clinical-gray rounded-md flex items-center gap-2.5 min-h-[40px] transition-colors" data-id="element-96">
                        <SettingsIcon size={16} className="text-text-secondary shrink-0" data-id="element-97" />
                        Impostazioni
                      </button>}
                    <button onClick={() => {
                onCloseTablet();
                setProfileMenuOpen(false);
              }} className="w-full text-left px-3 py-2.5 text-sm text-status-error hover:bg-status-error-bg rounded-md flex items-center gap-2.5 min-h-[40px] transition-colors" data-id="element-98">
                      <LogOutIcon size={16} className="shrink-0" data-id="element-99" />
                      Log-out
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div> : <div className="p-4" data-id="element-100">
            <div className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-clinical-gray transition-colors cursor-pointer min-h-[44px]" data-id="element-101">
              <div className="w-8 h-8 shrink-0 rounded-full bg-medical-green text-white flex items-center justify-center text-sm font-medium" data-id="element-102">
                {role === 'superadmin' ? 'SA' : role === 'medico' ? 'MD' : 'PZ'}
              </div>
              <div className="flex-1 min-w-0" data-id="element-103">
                <p className="text-sm font-medium text-text-primary truncate capitalize" data-id="element-104">
                  {role}
                </p>
                <p className="text-xs text-text-secondary truncate" data-id="element-105">
                  {role === 'superadmin' ? 'Amministratore di sistema' : role === 'medico' ? 'Medico Refertatore' : 'Area Personale'}
                </p>
              </div>
            </div>
          </div>}
      </div>
    </aside>;
  // Desktop sidebar — always visible, full width, fixed
  const desktopSidebar = <aside className="bg-white shadow-sidebar flex flex-col z-40 h-full w-[260px]" data-id="element-106">
      {/* Logo Area */}
      <div className="h-16 flex items-center border-b border-clinical-gray-100 shrink-0 px-6" data-id="element-107">
        <div className="flex flex-col justify-center w-full" data-id="element-108">
          <div className="flex items-start" data-id="element-109">
            <span className="text-xl font-bold text-text-primary tracking-tight" data-id="element-110">
              HEALTH
            </span>
            <span className="text-sm font-bold text-medical-green leading-none mt-1" data-id="element-111">
              1
            </span>
          </div>
          <span className="text-xs text-text-secondary font-medium truncate" data-id="element-112">
            Modulo Diagnostico
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 no-scrollbar" data-id="element-113">
        {filteredNavItems.map(item => <NavLink key={item.path} to={item.path} className={({
        isActive
      }) => `
              flex items-center gap-3 px-3 py-2.5 rounded-md text-body font-medium transition-colors group relative min-h-[44px]
              ${isActive ? 'bg-medical-green-50 text-medical-green' : 'text-text-secondary hover:bg-clinical-gray hover:text-text-primary'}
            `} data-id="element-114">
            {({
          isActive
        }) => <>
                {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-medical-green rounded-r-full" data-id="element-115" />}
                <item.icon size={20} className={`shrink-0 ${isActive ? 'text-medical-green' : 'text-text-tertiary group-hover:text-text-secondary'}`} data-id="element-116" />
                <span className="truncate" data-id="element-117">{item.label}</span>
              </>}
          </NavLink>)}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-clinical-gray-100 shrink-0" data-id="element-118">
        <div className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-clinical-gray transition-colors cursor-pointer min-h-[44px]" data-id="element-119">
          <div className="w-8 h-8 shrink-0 rounded-full bg-medical-green text-white flex items-center justify-center text-sm font-medium" data-id="element-120">
            {role === 'superadmin' ? 'SA' : role === 'medico' ? 'MD' : 'PZ'}
          </div>
          <div className="flex-1 min-w-0" data-id="element-121">
            <p className="text-sm font-medium text-text-primary truncate capitalize" data-id="element-122">
              {role}
            </p>
            <p className="text-xs text-text-secondary truncate" data-id="element-123">
              {role === 'superadmin' ? 'Amministratore di sistema' : role === 'medico' ? 'Medico Refertatore' : 'Area Personale'}
            </p>
          </div>
        </div>
      </div>
    </aside>;
  return <>
      {/* Desktop Sidebar (Fixed, always visible) */}
      <div className="hidden xl:block fixed left-0 top-0 h-screen z-30" data-id="element-124">
        {desktopSidebar}
      </div>

      {/* Tablet Sidebar (Overlay) */}
      <AnimatePresence data-id="element-125">
        {isTabletOpen && isTablet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-text-primary/40 backdrop-blur-sm z-40 hidden md:block xl:hidden" onClick={onCloseTablet} data-id="element-126" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 260
        }} className="fixed inset-y-0 left-0 z-50 hidden md:block xl:hidden" data-id="element-127">
              {sidebarContentFull}
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Mobile Sidebar (Overlay) */}
      <AnimatePresence data-id="element-128">
        {isMobileOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-text-primary/40 backdrop-blur-sm z-40 md:hidden" onClick={onCloseMobile} data-id="element-129" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-y-0 left-0 z-50 md:hidden" data-id="element-130">
              {sidebarContentFull}
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}